import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios'
import i18n from '@/i18n/config'

// Runtime configuration support - can be changed after deployment
declare global {
  interface Window {
    APP_CONFIG?: {
      API_BASE_URL: string
    }
  }
}

const getApiBaseUrl = () => {
  // In dev, ignore public/config.js production URL so UI labels and API hit the same DB you seed locally.
  if (import.meta.env.DEV) {
    return import.meta.env.VITE_API_BASE_URL || 'https://sakeena.life:4090/api'
  }
  // Production: runtime config (config.js), then build-time env, then default
  return (
    window.APP_CONFIG?.API_BASE_URL ||
    import.meta.env.VITE_API_BASE_URL ||
    'https://sakeena.life:4090/api'
  )
}

class ApiClient {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: getApiBaseUrl(), // Use function to get URL dynamically
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        
      },
    })

    this.setupInterceptors()
  }

  private setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // Update baseURL dynamically on each request
        config.baseURL = getApiBaseUrl()
        
        // Add auth token if available
        const token = localStorage.getItem('auth_token')
        if (config.headers) {
          if (token) config.headers.Authorization = `Bearer ${token}`
          config.headers['Accept-Language'] = i18n.language
        }
        return config
      },
      (error: AxiosError) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401 || error.response?.status === 403) {
          // Handle unauthorized - clear token and redirect to login
          localStorage.removeItem('auth_token')
          try { sessionStorage.setItem('session_expired', '1') } catch {}
          window.location.href = '/admin/login'
        }
        return Promise.reject(error)
      }
    )
  }

  get instance() {
    return this.client
  }

  // Convenience methods
  async get<T>(url: string, config?: any) {
    return this.client.get<T>(url, config)
  }

  async post<T>(url: string, data?: any, config?: any) {
    return this.client.post<T>(url, data, config)
  }

  async put<T>(url: string, data?: any, config?: any) {
    return this.client.put<T>(url, data, config)
  }

  async patch<T>(url: string, data?: any, config?: any) {
    return this.client.patch<T>(url, data, config)
  }

  async delete<T>(url: string, config?: any) {
    return this.client.delete<T>(url, config)
  }
}

export const apiClient = new ApiClient()


